// File: src/pages/NfpProfile.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nfpApi } from '../services/nfpApi';
import { apiFetch, setAuthToken, setOrgContext } from '../api/client';
import tealBack from '../Images/teal_dback.png';
import rypilLo from '../Images/rypilLo.png';
import { renderRypilText } from '../lib/rypilText';
import CauseConsumerPreview, { defaultHeroModeForImageMeta, normalizeHeroMode } from '../components/CauseConsumerPreview';

const PEACH = '#EBA477';
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB

async function inferHeroModeFromFile(file) {
  if (!file || typeof URL === 'undefined') return 'photo';
  const objectUrl = URL.createObjectURL(file);
  try {
    const meta = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({});
      img.src = objectUrl;
    });
    return defaultHeroModeForImageMeta(meta);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Merges checklist updates without accidentally downgrading a previously completed item.
const mergeChecklist = (setState, updates, { allowDowngrade = false } = {}) => {
  if (!updates) return;
  setState((prev) => {
    const next = { ...prev };
    Object.entries(updates).forEach(([key, value]) => {
      const bool = !!value;
      if (allowDowngrade || bool || typeof prev[key] === 'undefined') {
        next[key] = bool;
      }
    });
    return next;
  });
};

const normalizeReasons = (value) => (Array.isArray(value) ? value : []);

const deriveChecklistFromReasons = (reasonsRaw, fallback = {}) => {
  const reasons = new Set(normalizeReasons(reasonsRaw));
  const has = (key) => reasons.has(key);
  return {
    logo: has('missing_logo') ? false : !!fallback.logo,
    images: has('missing_images_min3') || has('missing_images') ? false : !!fallback.images,
    whySupport: has('missing_why_support') ? false : !!fallback.whySupport,
    mission: has('missing_mission') ? false : !!fallback.mission,
    areas: has('missing_areas') ? false : !!fallback.areas,
    termsSigned: has('signatory_not_signed') ? false : !!fallback.termsSigned,
    proofVerified: has('proof_unsubmitted') || has('proof_pending') || has('proof_rejected')
      ? false
      : !!fallback.proofVerified,
    moderationPassed: has('moderation_pending') || has('moderation_rejected')
      ? false
      : !!fallback.moderationPassed,
  };
};

const deriveListingStatus = (visibility) => {
  const state = visibility?.state || 'hidden_pending';
  const reasons = normalizeReasons(visibility?.reasons);
  const hasReviewOnly =
    reasons.length > 0 &&
    reasons.every((r) => r === 'proof_pending' || r === 'moderation_pending' || r === 'inactive');

  if (state === 'blocked_admin') {
    return { label: 'App listing: Blocked', color: '#F87171' };
  }
  if (state === 'suspended') {
    return { label: 'App listing: Suspended', color: '#FBBF24' };
  }
  if (state === 'visible') {
    return { label: 'App listing: Live', color: '#34D399' };
  }
  if (hasReviewOnly) {
    return { label: 'App listing: In review', color: '#FBBF24' };
  }
  return { label: 'App listing: Incomplete', color: '#F87171' };
};

const derivePaymentsStatus = (stripeStatus, payoutReadiness) => {
  if (stripeStatus !== 'active') {
    if (stripeStatus === 'pending') {
      return { label: 'Payments: Connected (pending)', color: '#FBBF24' };
    }
    return { label: 'Payments: Stripe not connected', color: '#F87171' };
  }
  if (payoutReadiness?.payoutReady) {
    return { label: 'Payments: Ready', color: '#34D399' };
  }
  return { label: 'Payments: Connected (not ready)', color: '#FBBF24' };
};

export default function NfpProfile() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [cause, setCause] = useState(null);
  const [stripeStatus, setStripeStatus] = useState('not_connected');
  const [visibility, setVisibility] = useState(null);
  const [payoutReadiness, setPayoutReadiness] = useState(null);
  const [, setChecklist] = useState({
    logo: false,
    images: false,
    whySupport: false,
    mission: false,
    areas: false,
    termsSigned: false,
    proofVerified: false,
    moderationPassed: false,
  });
  const [assets, setAssets] = useState({ logoUrl: '', images: [] });

  const applyDashboardUpdate = (resp) => {
    if (resp?.cause) setCause(resp.cause);
    if (resp?.visibility) setVisibility(resp.visibility);
    if (resp?.payoutReadiness) setPayoutReadiness(resp.payoutReadiness);
    if (resp?.stripeStatus) setStripeStatus(resp.stripeStatus);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setErr('');
        setLoading(true);
        const resp = await nfpApi.getDashboard();
        if (cancelled) return;

        const c = resp?.cause || null;
        const s = resp?.stripeStatus || 'not_connected';
        const cl = resp?.profileChecklist || {};
        const vis = resp?.visibility || c?.visibility || null;
        const readiness = resp?.payoutReadiness || null;
        const a = resp?.assets || {};

        setCause(c);
        setStripeStatus(s);
        setVisibility(vis);
        setPayoutReadiness(readiness);

        const derivedChecklist = deriveChecklistFromReasons(vis?.reasons, {
          logo: !!cl.logo,
          images: !!cl.images,
          whySupport: !!cl.whySupport,
          mission: !!cl.mission,
          areas: !!cl.areas,
          termsSigned: !!cl.termsSigned,
          proofVerified: !!cl.proofVerified,
          moderationPassed: !!cl.moderationPassed,
        });
        mergeChecklist(setChecklist, derivedChecklist, { allowDowngrade: true });

        setAssets({
          logoUrl: a.logoUrl || '',
          images: Array.isArray(a.images) ? a.images : [],
        });
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Failed to load profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const headerSubtitle = renderRypilText('Control how your organization appears to supporters in rypil');
  const listingStatus = deriveListingStatus(visibility);
  const paymentsStatus = derivePaymentsStatus(stripeStatus, payoutReadiness);

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {}
    try {
      setAuthToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('activeAccountId');
    } catch {}
    setOrgContext(null);
    navigate('/login');
  };

  return (
    <div style={styles.fullscreen}>
      <div style={styles.dashboardMain}>
        <div style={styles.dashboardInner}>
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.headerTitle}>{cause?.name || 'Your Organization'}</h1>
              {headerSubtitle ? <div style={styles.headerSub}>{headerSubtitle}</div> : null}
            </div>
            <div style={styles.headerRight}>
              <div style={styles.headerChips}>
                <StatusChip
                  label={paymentsStatus.label}
                  color={paymentsStatus.color}
                />
                <StatusChip label={listingStatus.label} color={listingStatus.color} />
              </div>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => navigate('/nfp')}
              >
                Home
              </button>
              <button type="button" style={styles.logoutBtn} onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>

          {err && <div style={styles.errorCard}>{err}</div>}

          {loading ? (
            <div style={styles.loadingBlock}>
              <div style={styles.spinner} />
              <p>Loading profile…</p>
            </div>
          ) : (
            <>
              <div style={styles.columnsProfile}>
                {/* LEFT: copy fields */}
                <div style={styles.column}>
                  <ShortDescriptionCard
                    value={cause?.shortDescription || ''}
                    onSave={async (text) => {
                      const resp = await nfpApi.saveAssets({ shortDescription: text });
                      applyDashboardUpdate(resp);
                      if (resp?.profileChecklist) {
                        mergeChecklist(setChecklist, resp.profileChecklist, {
                          allowDowngrade: false,
                        });
                      }
                      if (resp?.assets) setAssets(resp.assets);
                    }}
                  />

                  <WhySupportCard
                    value={cause?.consumerProfile?.whySupportShort || cause?.whySupport || ''}
                    onSave={async (txt) => {
                      const resp = await nfpApi.saveWhySupport({ text: txt });
                      if (resp?.profileChecklist) {
                        mergeChecklist(setChecklist, resp.profileChecklist, {
                          allowDowngrade: false,
                        });
                      }
                      if (resp?.cause || resp?.visibility || resp?.payoutReadiness) {
                        applyDashboardUpdate(resp);
                      } else {
                        setCause((prev) => {
                          if (!prev) return prev;
                          const next = { ...prev };
                          next.consumerProfile = {
                            ...(prev.consumerProfile || {}),
                            whySupportShort: txt,
                          };
                          next.whySupport = txt;
                          return next;
                        });
                      }
                    }}
                  />
                </div>

                {/* RIGHT: assets + live preview */}
                <div style={styles.column}>
                  <AssetsCard
                    assets={assets}
                    onUploadLogo={async (file) => {
                      const uploadResp = await nfpApi.uploadAsset(file, { kind: 'logo' });
                      const uploadedUrl = uploadResp?.url || uploadResp?.location;
                      setAssets((a) => ({ ...a, logoUrl: uploadedUrl || a.logoUrl }));
                      mergeChecklist(setChecklist, uploadResp?.profileChecklist, {
                        allowDowngrade: false,
                      });
                      const hasLogo = !!(uploadedUrl || assets.logoUrl);
                      mergeChecklist(
                        setChecklist,
                        { logo: hasLogo },
                        { allowDowngrade: false }
                      );
                      if (uploadedUrl) {
                        const resp = await nfpApi.saveAssets({ logoUrl: uploadedUrl });
                        if (resp?.assets) setAssets(resp.assets);
                        applyDashboardUpdate(resp);
                        if (resp?.profileChecklist) {
                          mergeChecklist(setChecklist, resp.profileChecklist, {
                            allowDowngrade: false,
                          });
                        }
                      }
                    }}
                    onUploadImages={async (files) => {
                      const uploaded = [];
                      for (const f of files) {
                        const heroMode = await inferHeroModeFromFile(f);
                        const { url, id, _id, profileChecklist } = await nfpApi.uploadAsset(f, {
                          kind: 'image',
                        });
                        if (url) uploaded.push({ id: id || _id, url, heroMode, isHero: false });
                        mergeChecklist(setChecklist, profileChecklist, { allowDowngrade: false });
                      }
                      let nextCount = assets.images?.length || 0;
                      let nextImages = [];
                      setAssets((a) => ({
                        ...a,
                        images: (() => {
                          const next = [...a.images, ...uploaded].slice(0, 5);
                          nextCount = next.length;
                          nextImages = next;
                          return next;
                        })(),
                      }));
                      const imagesDone = nextCount >= 3;
                      mergeChecklist(
                        setChecklist,
                        { images: imagesDone },
                        { allowDowngrade: false }
                      );
                      const resp = await nfpApi.saveAssets({ images: nextImages });
                      if (resp?.assets) setAssets(resp.assets);
                      applyDashboardUpdate(resp);
                      if (resp?.profileChecklist) {
                        mergeChecklist(setChecklist, resp.profileChecklist, {
                          allowDowngrade: false,
                        });
                      }
                    }}
                    onPickHero={async (image, fallbackIdx) => {
                      if (!image) return;
                      const heroId = image.id || image._id || null;
                      const imgs = Array.isArray(assets.images) ? [...assets.images] : [];
                      const matchKey = heroId || image.url;
                      const matchFn = (img) =>
                        img.id === matchKey || img._id === matchKey || img.url === matchKey;
                      let idx = imgs.findIndex(matchFn);
                      if (idx < 0 && typeof fallbackIdx === 'number') idx = fallbackIdx;
                      if (idx < 0) return;
                      const nextImages = imgs.map((img, i) => ({
                        ...img,
                        heroMode: i === idx ? normalizeHeroMode(img.heroMode) : img.heroMode,
                        isHero: i === idx,
                      }));
                      setAssets((prev) => ({ ...prev, images: nextImages }));

                      const resp = await nfpApi.saveAssets({ images: nextImages });
                      if (resp?.assets) setAssets(resp.assets);
                      applyDashboardUpdate(resp);
                      if (resp?.profileChecklist) {
                        mergeChecklist(setChecklist, resp.profileChecklist, {
                          allowDowngrade: false,
                        });
                      }
                    }}
                  />

                  <LivePreviewCard
                    images={assets.images}
                    name={cause?.name}
                    shortDescription={
                      cause?.shortDescription || cause?.consumerProfile?.shortDescription
                    }
                    whySupport={cause?.consumerProfile?.whySupportShort || cause?.whySupport}
                    categoryLabel={(cause?.missionCategories || cause?.consumerProfile?.missionCategories || [])[0] || cause?.category || 'Cause'}
                    impactLabel={cause?.classification || 'Local Impact'}
                    onSetHeroMode={async (mode) => {
                      const imgs = Array.isArray(assets.images) ? [...assets.images] : [];
                      const heroIdx = imgs.findIndex((img) => !!img?.isHero);
                      if (heroIdx < 0) return;
                      const nextImages = imgs.map((img, idx) => (
                        idx === heroIdx ? { ...img, heroMode: normalizeHeroMode(mode) } : img
                      ));
                      setAssets((prev) => ({ ...prev, images: nextImages }));
                      const resp = await nfpApi.saveAssets({ images: nextImages });
                      if (resp?.assets) setAssets(resp.assets);
                      applyDashboardUpdate(resp);
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <img src={rypilLo} alt="rypil mark" style={styles.badge} />
    </div>
  );
}

/** ===== Shared small components for profile screen ===== */

function StatusChip({ label, color }) {
  return (
    <span style={{ ...styles.statusChip, borderColor: color, color }}>
      <span style={{ ...styles.statusDot, background: color }} />
      {label}
    </span>
  );
}

function Card({ title, right, children, subtitle, style }) {
  return (
    <section style={{ ...styles.card, ...(style || {}) }}>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{title}</div>
          {subtitle && <div style={styles.cardSubtitle}>{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function ShortDescriptionCard({ value, onSave }) {
  const [text, setText] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setText(value || '');
  }, [value]);

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await onSave(text);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e?.message || 'Save failed.');
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(''), 2000);
    }
  };

  return (
    <Card
      title="Short description"
      subtitle="A one-line summary shown on your profile card"
    >
      <input
        style={styles.input}
        maxLength={140}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g., Planting trees and restoring local green spaces in our city."
      />
      <div style={styles.cardActions}>
        <span style={styles.messageText}>{text.length}/140</span>
        <button
          style={{ ...styles.primaryBtn, marginLeft: 'auto' }}
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {!!msg && <span style={styles.messageText}>{msg}</span>}
      </div>
    </Card>
  );
}

function WhySupportCard({ value, onSave }) {
  const [text, setText] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setText(value || '');
  }, [value]);

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await onSave(text);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e?.message || 'Save failed.');
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(''), 2000);
    }
  };

  return (
    <Card
      title="Why supporters should choose you"
      subtitle="Appears on your full cause profile"
      style={styles.tallBandCard}
    >
      <textarea
        style={{ ...styles.input, minHeight: 220, resize: 'vertical' }}
        maxLength={800}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="In 2–3 sentences, explain your mission and why everyday purchases through rypil matter for your cause."
      />
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 12 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>{text.length}/800</span>
        <button
          style={{ ...styles.primaryBtn, marginLeft: 'auto' }}
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {!!msg && <span style={styles.messageText}>{msg}</span>}
      </div>
    </Card>
  );
}

function AssetsCard({ assets, onUploadLogo, onUploadImages, onPickHero }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const logoInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setMsg('Image too large. Please choose a file that is 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await onUploadLogo(file);
      setMsg('Logo uploaded.');
    } catch (e2) {
      setMsg(e2?.message || 'Upload failed.');
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooBig) {
      setMsg('Image too large. Please choose files that are 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await onUploadImages(files);
      setMsg('Images uploaded.');
    } catch (e2) {
      setMsg(e2?.message || 'Upload failed.');
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const imageList = Array.isArray(assets.images) ? assets.images : [];
  const imageCount = imageList.length;
  const inRange = imageCount >= 3 && imageCount <= 5;
  const explicitHero = imageList.find((img) => !!img?.isHero) || null;
  const heroKey = explicitHero?.id || explicitHero?._id || explicitHero?.url || null;
  const matchesHero = (img) => {
    if (!heroKey) return false;
    return img.id === heroKey || img._id === heroKey || img.url === heroKey;
  };

  return (
    <Card
      title="Profile assets"
      subtitle="Logo is saved for future use. Choose one image as the consumer profile hero."
      right={
        <span style={{ fontSize: 12, opacity: 0.75 }}>
          {imageCount} image{imageCount === 1 ? '' : 's'}
        </span>
      }
    >
      <div style={styles.assetStack}>
        <div>
          <div style={styles.assetLabel}>Organization logo</div>
          <p style={styles.mutedText}>
            Stored for identity and future placements. The consumer app profile header currently uses your cause category icon.
          </p>
          {assets.logoUrl ? (
            <img src={assets.logoUrl} alt="Logo" style={styles.logoImage} />
          ) : (
            <div style={styles.logoEmpty}>No logo yet</div>
          )}
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            style={styles.hiddenFileInput}
            onChange={handleLogo}
            disabled={busy}
          />
          <button
            type="button"
            style={styles.uploadBtn}
            onClick={() => logoInputRef.current?.click()}
            disabled={busy}
          >
            {busy ? 'Uploading…' : assets.logoUrl ? 'Change logo' : 'Upload logo'}
          </button>
        </div>

        <div>
          <div style={styles.assetLabel}>Consumer profile images (3–5)</div>
          <p style={styles.mutedText}>
            Choose one image as the consumer profile hero. Photos can crop; graphics and text-heavy creative should use Graphic display in the preview.
          </p>
          {!heroKey ? (
            <p style={styles.mutedText}>No hero selected yet. Choose one image as your hero.</p>
          ) : null}
          <div style={styles.assetGrid}>
            {imageList.map((img, idx) => {
              const isHero = matchesHero(img);
              return (
                <div
                  key={img.id || img.url}
                  style={{
                    ...styles.assetThumb,
                    borderColor: isHero
                      ? '#A7F3D0'
                      : 'rgba(148,163,184,0.7)',
                    boxShadow: isHero
                      ? '0 0 0 2px rgba(16,185,129,0.35)'
                      : undefined,
                  }}
                >
                  <img src={img.url} alt="Asset" style={styles.assetThumbImg} />
                  {isHero && <div style={styles.heroBadge}>Hero image</div>}
                  <button
                    type="button"
                    style={styles.assetThumbAction}
                    onClick={() => {
                      if (!isHero) onPickHero?.(img, idx);
                    }}
                    disabled={busy || isHero}
                  >
                    {isHero ? 'Current hero' : 'Set as hero'}
                  </button>
                </div>
              );
            })}
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            style={styles.hiddenFileInput}
            onChange={handleImages}
            disabled={busy || imageCount >= 5}
          />
          <div style={styles.uploadRow}>
            <button
              type="button"
              style={styles.uploadBtn}
              onClick={() => imageInputRef.current?.click()}
              disabled={busy || imageCount >= 5}
            >
              {busy
                ? 'Uploading…'
                : imageCount >= 5
                ? 'Image limit reached'
                : 'Upload images'}
            </button>
            <span style={styles.uploadHint}>Max image size 5 MB</span>
          </div>
          <div
            style={{
              fontSize: 12,
              marginTop: 6,
              opacity: inRange ? 0.8 : 1,
              color: inRange ? '#A7F3D0' : '#FBBF24',
            }}
          >
            {inRange
              ? 'Looking good—3–5 images will give supporters a strong sense of your work.'
              : 'Aim for at least 3 images and no more than 5 to complete your profile.'}
          </div>
        </div>

        {!!msg && <div style={{ ...styles.bodyText, marginTop: 4 }}>{msg}</div>}
      </div>
    </Card>
  );
}

function LivePreviewCard({
  images,
  name,
  shortDescription,
  whySupport,
  categoryLabel,
  impactLabel,
  onSetHeroMode,
}) {
  const imageCount = Array.isArray(images) ? images.length : 0;
  const heroImage = Array.isArray(images) ? images.find((img) => !!img?.isHero) : null;
  const hero = heroImage?.url || '';
  const heroMode = normalizeHeroMode(heroImage?.heroMode);
  const needsHeroSelection = imageCount === 0 || !hero;

  const shortDescPreview =
    shortDescription || 'Use a short description to quickly tell supporters who you are.';
  const whySupportPreview =
    whySupport || 'Share the impact supporters make and why this cause matters.';

  return (
    <Card
      title="Consumer Profile Preview"
      subtitle={renderRypilText('This preview uses the same profile contract consumers see in rypil. Logos are saved for future use, but the app header uses the category icon.')}
      style={styles.tallBandCard}
    >
      <div style={styles.previewDevice}>
        <CauseConsumerPreview
          name={name}
          impactLabel={impactLabel}
          categoryLabel={categoryLabel}
          missionText={shortDescPreview}
          whySupportText={whySupportPreview}
          heroUrl={hero}
          heroMode={heroMode}
          showSupportButton
          style={styles.previewCardReset}
        />

          {needsHeroSelection ? (
            <div style={styles.heroWarning}>
              Select a consumer profile hero image. Upload images first, then choose one as hero.
            </div>
          ) : null}

          {hero && onSetHeroMode ? (
            <div style={styles.previewModeRow}>
              <span style={styles.sliderLabel}>Hero display</span>
              {['photo', 'graphic'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  style={{
                    ...styles.previewModeButton,
                    ...(heroMode === mode ? styles.previewModeButtonActive : null),
                  }}
                  onClick={() => onSetHeroMode(mode)}
                >
                  {mode === 'photo' ? 'Photo' : 'Graphic'}
                </button>
              ))}
            </div>
          ) : null}

      </div>
    </Card>
  );
}

/** ===== Styles (mirrors dashboard so it feels consistent) ===== */

const styles = {
  fullscreen: {
    backgroundImage: `url(${tealBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '100vh',
    width: '100%',
    color: 'white',
    fontFamily: 'Helvetica, Arial, sans-serif',
  },
  dashboardMain: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: 80,
  },
  dashboardInner: {
    width: 'min(1280px, 100%)',
    padding: '48px 24px 120px',
    display: 'grid',
    gap: 24,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 700,
    margin: 0,
  },
  headerSub: {
    fontSize: 14,
    opacity: 0.8,
  },
  headerChips: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  headerRight: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  secondaryBtn: {
    borderRadius: 999,
    border: 'none',
    background: PEACH,
    color: '#1F2933',
    padding: '8px 18px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 13,
  },
  logoutBtn: {
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.55)',
    background: 'rgba(15,23,42,0.55)',
    color: 'white',
    padding: '8px 18px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorCard: {
    background: 'rgba(248,113,113,0.12)',
    border: '1px solid rgba(248,113,113,0.45)',
    color: '#FCA5A5',
    padding: '10px 14px',
    borderRadius: 16,
  },
  loadingBlock: {
    display: 'grid',
    gap: 12,
    placeItems: 'center',
    padding: 32,
    borderRadius: 16,
    border: '1px solid rgba(148,163,184,0.35)',
    background: 'rgba(15,23,42,0.7)',
    backdropFilter: 'blur(16px)',
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.25)',
    borderTopColor: PEACH,
    animation: 'spin 1s linear infinite',
  },
  columnsProfile: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'minmax(320px, 0.85fr) minmax(360px, 1.15fr)',
    alignItems: 'flex-start',
  },
  column: {
    display: 'grid',
    gap: 24,
    alignSelf: 'flex-start',
  },
  card: {
    borderRadius: 22,
    border: '1px solid rgba(148,163,184,0.45)',
    background: 'linear-gradient(145deg, rgba(15,23,42,0.7), rgba(31,41,55,0.55))',
    padding: 18,
    display: 'grid',
    gap: 12,
    backdropFilter: 'blur(22px)',
    boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
  },
  tallBandCard: {
    minHeight: 360,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  cardSubtitle: {
    fontSize: 13,
    opacity: 0.72,
    marginTop: 4,
  },
  primaryBtn: {
    background: PEACH,
    border: 'none',
    color: '#1F2933',
    borderRadius: 999,
    padding: '8px 16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  bodyText: {
    fontSize: 13,
    opacity: 0.85,
  },
  mutedText: {
    fontSize: 13,
    opacity: 0.65,
    marginBottom: 4,
  },
  input: {
    width: '100%',
    borderRadius: 12,
    border: '1px solid rgba(148,163,184,0.6)',
    background: 'rgba(15,23,42,0.8)',
    color: 'white',
    padding: '8px 10px',
    boxSizing: 'border-box',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
  },
  messageText: {
    fontSize: 12,
    opacity: 0.7,
  },
  badge: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 48,
    opacity: 0.95,
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    border: '1px solid',
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  assetStack: {
    display: 'grid',
    gap: 16,
  },
  assetLabel: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
  },
  logoImage: {
    height: 56,
    borderRadius: 12,
    border: '1px solid rgba(148,163,184,0.7)',
    background: 'white',
    objectFit: 'contain',
    padding: 8,
  },
  logoEmpty: {
    height: 56,
    borderRadius: 12,
    border: '1px solid rgba(148,163,184,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    opacity: 0.7,
  },
  hiddenFileInput: {
    display: 'none',
  },
  uploadBtn: {
    marginTop: 10,
    padding: '8px 12px',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.3)',
    background: 'rgba(15,23,42,0.55)',
    color: 'white',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  uploadRow: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  uploadHint: {
    fontSize: 12,
    opacity: 0.75,
  },
  assetGrid: {
    display: 'grid',
    gap: 10,
    gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
  },
  assetThumb: {
    position: 'relative',
    border: '1px solid rgba(148,163,184,0.7)',
    borderRadius: 12,
    overflow: 'hidden',
    background: 'rgba(15,23,42,0.7)',
  },
  assetThumbImg: {
    width: '100%',
    height: 96,
    objectFit: 'cover',
    display: 'block',
  },
  heroBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    background: 'rgba(16,185,129,0.9)',
    color: '#052e16',
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 999,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  assetThumbAction: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    right: 6,
    border: 'none',
    borderRadius: 999,
    padding: '6px 0',
    fontSize: 12,
    fontWeight: 600,
    background: 'rgba(15,23,42,0.85)',
    color: 'white',
    cursor: 'pointer',
  },
  previewDevice: {
    maxWidth: 340,
    margin: '0 auto',
    padding: 16,
    borderRadius: 28,
    border: '1px solid rgba(148,163,184,0.35)',
    background: 'rgba(15,23,42,0.6)',
    boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
  },
  previewCard: {
    border: '1px solid rgba(148,163,184,0.4)',
    borderRadius: 24,
    overflow: 'hidden',
    background: '#183C41',
  },
  previewCardReset: {
    width: '100%',
    minHeight: 0,
    borderRadius: 24,
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
  },
  previewLogoFrame: {
    width: 48,
    height: 48,
    display: 'grid',
    placeItems: 'center',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    overflow: 'hidden',
    flexShrink: 0,
  },
  previewLogo: {
    width: 44,
    height: 44,
    objectFit: 'contain',
    borderRadius: 999,
    background: '#E1E8EF',
  },
  previewLogoPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 999,
    background: '#cfd6dd',
  },
  previewText: {
    minWidth: 0,
    display: 'grid',
    gap: 4,
    paddingLeft: 4,
  },
  previewName: {
    fontWeight: 600,
  },
  previewSubtitle: {
    fontSize: 13,
    opacity: 0.85,
  },
  previewHeroWrap: {
    position: 'relative',
    width: '100%',
    paddingTop: '56%',
    overflow: 'hidden',
  },
  previewHeroBg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '50% 50%',
    transition:
      'background-size 120ms ease, background-position 120ms ease, transform 120ms ease',
  },
  focusHandle: {
    position: 'absolute',
    width: 0,
    height: 0,
    pointerEvents: 'none',
  },
  focusDot: {
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '2px solid #A7F3D0',
    background: 'rgba(15,23,42,0.7)',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0 0 0 2px rgba(16,185,129,0.35)',
    cursor: 'grab',
    touchAction: 'none',
    pointerEvents: 'auto',
  },
  previewHeroFallback: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
  heroWarning: {
    margin: '10px 12px 4px',
    borderRadius: 10,
    border: '1px solid rgba(251,191,36,0.65)',
    background: 'rgba(251,191,36,0.12)',
    color: '#FDE68A',
    fontSize: 12,
    fontWeight: 600,
    padding: '8px 10px',
  },
  previewSliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px 4px',
  },
  previewModeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 12px 4px',
    flexWrap: 'wrap',
  },
  previewModeButton: {
    border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: 8,
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    padding: '7px 10px',
    cursor: 'pointer',
  },
  previewModeButtonActive: {
    background: PEACH,
    borderColor: PEACH,
    color: '#102E35',
  },
  sliderLabel: {
    fontSize: 12,
    opacity: 0.7,
    minWidth: 36,
  },
  previewWhySection: {
    borderTop: '1px solid rgba(148,163,184,0.35)',
    padding: '12px 14px 16px',
    display: 'grid',
    gap: 6,
  },
  previewWhyLabel: {
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    opacity: 0.7,
  },
  previewWhyText: {
    fontSize: 13,
    lineHeight: 1.5,
    opacity: 0.9,
  },
};

if (typeof document !== 'undefined' && !document.getElementById('nfp-profile-spin')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'nfp-profile-spin';
  styleEl.innerHTML =
    '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(styleEl);
}
